import { useMemo, useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { displayName } from '@promanage/contracts/person-display';
import { canViewTrustPosition, formatRef, parseRef, type PortfolioScope } from '@promanage/core';
import { Amount, Button } from '@promanage/ui';
import { ColumnPicker } from '../features/table/ColumnPicker';
import { DataTable } from '../features/table/DataTable';
import { EmptyState, FilterChips, ListShell } from '../features/table/ListShell';
import { compareRows, type SortState } from '../features/table/columns';
import { useColumnState } from '../features/table/useColumnState';
import {
  DEFAULT_OWNER_SORT,
  OWNER_COLUMNS,
  type OwnerColumnId,
} from '../features/property/ownerColumns';
import type { OwnerOption } from '../features/property/types';
import { trpc } from '../lib/trpc';
import { portfolioScopeFor, usePortfolioScope } from '../features/portfolio/scope';
import { resolveSession, useSession } from '../features/auth/session';
import { WorkspaceMenu, useWorkspace } from '../features/workspaces/WorkspaceMenu';
import { workspaceChoice, workspaceTable, workspaceText } from '../features/workspaces/state';

export const Route = createFileRoute('/owners/')({
  loader: async ({ context }) => {
    const session = await resolveSession(context.queryClient);
    const scope = portfolioScopeFor(session);

    await Promise.all([
      context.queryClient.prefetchQuery(ownerListOptions(scope)),
      // The control total is a trust admin's — see `OwnerControlBalance`.
      canViewTrustPosition(session?.roles ?? [])
        ? context.queryClient.prefetchQuery(ownerPositionOptions())
        : null,
    ]);
  },
  component: OwnersPage,
});

const ownerListOptions = (scope: PortfolioScope) =>
  trpc.property.owners.queryOptions({ scope }, { staleTime: 10_000 });

const ownerPositionOptions = () =>
  trpc.ledger.position.queryOptions(undefined, { staleTime: 10_000 });

const STORAGE_KEY = 'promanage:owner-columns';

/** One identity, so the pending state does not re-sort the table on every render. */
const NO_OWNERS: OwnerOption[] = [];

type OwnerFilter = 'all' | 'managing' | 'unattached';

const FILTERS: { value: OwnerFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'managing', label: 'With properties' },
  { value: 'unattached', label: 'Nobody attached' },
];

/**
 * Owner entities — the things that hold the money.
 *
 * An owner is an ENTITY, not a person: "Solomona Holdings Ltd" and the "R & K Beauchamp Trust"
 * hold the bank account, the GST number and the trust ledger account. The humans behind them
 * attach by role and live under People, which is why "Contacts" is a column of links rather
 * than an email address. A sole natural-person owner is an entity with one person attached —
 * that is why there is no "first name" field anywhere here and never will be.
 *
 * "Nobody attached" is a filter because it is a real backlog: an entity with no human behind
 * it is a statement nobody can be sent.
 */
function OwnersPage() {
  const navigate = useNavigate();
  const { scope, setScope, canToggle } = usePortfolioScope();
  const mayViewPosition = canViewTrustPosition(useSession()?.roles ?? []);
  const list = useQuery({
    ...ownerListOptions(scope),
    meta: { errorTitle: 'Could not load the owners' },
  });
  const owners = list.data ?? NO_OWNERS;

  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<OwnerFilter>('all');
  const [sort, setSort] = useState<SortState<OwnerColumnId>>(DEFAULT_OWNER_SORT);
  const columns = useColumnState(STORAGE_KEY, OWNER_COLUMNS);
  const workspace = useWorkspace({
    route: '/owners',
    state: { filters: { query, filter, scope }, columns: columns.state.order, sort },
    apply(state) {
      const table = workspaceTable(state, OWNER_COLUMNS, DEFAULT_OWNER_SORT);
      setQuery(workspaceText(state, 'query'));
      setFilter(
        workspaceChoice(
          state,
          'filter',
          FILTERS.map((option) => option.value),
          'all',
        ),
      );
      if (canToggle) setScope(workspaceChoice(state, 'scope', ['own', 'agency'], 'own'));
      columns.setState(table.columns);
      setSort(table.sort);
    },
  });

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    return owners
      .filter((owner) => {
        if (filter === 'managing') return owner.propertyCount > 0;
        if (filter === 'unattached') return owner.contacts.length === 0;
        return true;
      })
      .filter((owner) => {
        if (!q) return true;

        const asRef = parseRef(q);
        if (asRef) return asRef.entity === 'owner' && asRef.sequence === owner.refSeq;

        return [
          owner.name,
          formatRef('owner', owner.refSeq),
          owner.gstNumber,
          ...owner.contacts.map(displayName),
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()
          .includes(q);
      })
      .sort((a, b) => compareRows(a, b, sort, OWNER_COLUMNS));
  }, [owners, query, filter, sort]);

  const total = owners.length;
  const summary = list.isPending
    ? 'Loading…'
    : rows.length === total
      ? `${total} owner ${total === 1 ? 'entity' : 'entities'}`
      : `Showing ${rows.length} of ${total} owner entities`;

  return (
    <ListShell
      title="Owners"
      subtitle={summary}
      meta={mayViewPosition ? <OwnerControlBalance /> : null}
      actions={
        <>
          <WorkspaceMenu workspace={workspace} />
          <ColumnPicker
            columns={OWNER_COLUMNS}
            state={columns.state}
            onChange={columns.setState}
            onReset={columns.reset}
          />
          <Link to="/owners/new">
            <Button size="sm">
              <span aria-hidden="true">+</span> Add owner
            </Button>
          </Link>
        </>
      }
      search={query}
      onSearchChange={setQuery}
      searchPlaceholder="Search name, ref (OW000001), GST number or contact"
      searchLabel="Search owners"
      filters={
        <FilterChips label="Filter owners" value={filter} options={FILTERS} onChange={setFilter} />
      }
      isPending={list.isPending}
      error={list.error}
      onRetry={() => void list.refetch()}
      isEmpty={rows.length === 0}
      empty={
        <EmptyState
          title={query ? `Nothing matches “${query}”` : 'No owners yet'}
          action={
            <Link to="/owners/new">
              <Button size="sm">Add owner</Button>
            </Link>
          }
        >
          {query
            ? 'Try a different name, ref, GST number or contact.'
            : 'An owner entity holds a trust ledger account. A property cannot exist without at least one, because there would be nobody for the rent to belong to.'}
        </EmptyState>
      }
    >
      {/*
       * The whole row opens the record, over and above the link in the name cell — the Contacts
       * cell still links to the people, so the two targets inside a row go to different places
       * on purpose.
       */}
      <DataTable
        rows={rows}
        columns={columns.visible}
        sort={sort}
        onSortChange={setSort}
        rowKey={(owner) => owner.id}
        onOpen={(owner) =>
          void navigate({ to: '/owners/$ref', params: { ref: formatRef('owner', owner.refSeq) } })
        }
        caption={`Owner entities, one row per entity. ${summary}.`}
        minWidth="52rem"
      />
    </ListShell>
  );
}

/**
 * The owner control account — every owner's balance added up, beside the page title.
 *
 * The same total `/financial` shows as one line of the trust position, read here from the same
 * `ledger.position` query so there is no second way of summing an owner's worth of accounts.
 * This is the CONTROL total, not the sum of the rows on screen: it does not move when the list
 * below is searched or filtered, because the number that has to reconcile against the bank is
 * the whole agency's, not a subset of it.
 *
 * Which is why it sits in `meta` beside the title rather than above the Balance column, where
 * it would read as that column's total the moment anybody filtered.
 *
 * A TRUST ADMIN'S, for the reason in `canViewTrustPosition`: it is every owner's money added up,
 * the agency's figure rather than anybody's working one. Each owner's own balance stays in the
 * table for everyone.
 */
function OwnerControlBalance() {
  const position = useQuery(ownerPositionOptions());
  const group = position.data?.groups.find((g) => g.type === 'owner');

  // Silent rather than an error banner: this is context for the page, not the page itself, and
  // the table underneath still loads and reports its own errors. Also covers the agency that has
  // never opened an owner account — nothing to add up yet.
  if (position.error || (!position.isPending && !group)) return null;

  return (
    <div className="leading-tight whitespace-nowrap">
      {/*
       * Not "Balance": there are balances in the column below and they are a different,
       * filterable set of numbers. The count it covers is the subtitle two lines left of here.
       */}
      <span className="block text-[0.6875rem] text-ink-subtle">
        {group?.name ?? 'Owner control account'}
      </span>

      {/*
       * Same two lines whether it has loaded or not, so the bar does not resize under the
       * pointer on the way to the Columns button.
       */}
      {group ? (
        <Amount
          // Plain `BigInt`, as the owner columns do. `centsFromWire` is the same call wrapped,
          // and importing it would pull the posting form's arithmetic into this route's chunk.
          value={BigInt(group.balanceCents)}
          currency
          tone="auto"
          className="text-[0.9375rem] font-semibold"
        />
      ) : (
        <span className="text-[0.9375rem] font-medium text-ink-subtle">Loading…</span>
      )}
    </div>
  );
}
